import { useEffect, useRef } from 'react'

const FOCUSABLE_SELECTOR = [
  '[autofocus]',
  '[data-fallback-focus]',
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

let lastInteractionFocusable = null
let hasInstalledInteractionListener = false

const installInteractionListenerOnce = () => {
  if (hasInstalledInteractionListener) return
  if (typeof document === 'undefined') return

  const captureLastFocusable = event => {
    const target = event && event.target
    if (!(target instanceof Element)) return

    const focusable = target.closest(FOCUSABLE_SELECTOR)
    if (focusable instanceof HTMLElement) {
      lastInteractionFocusable = focusable
    }
  }

  document.addEventListener('mousedown', captureLastFocusable, true)
  document.addEventListener('touchstart', captureLastFocusable, true)
  document.addEventListener('pointerdown', captureLastFocusable, true)
  hasInstalledInteractionListener = true
}

const isFocusableVisible = element => {
  if (!(element instanceof HTMLElement)) return false
  if (element.hasAttribute('disabled')) return false
  if (element.getAttribute('aria-hidden') === 'true') return false

  const style = window.getComputedStyle(element)
  if (style.display === 'none' || style.visibility === 'hidden') return false

  const rect = element.getBoundingClientRect()
  if (rect.width === 0 && rect.height === 0) return false

  return true
}

const EXISTING_FORM_OVERLAY_SELECTOR = '.dhsv_wertermittler__existing_form.open'

const getExistingFormOverlay = container => {
  if (!(container instanceof Element)) return null
  return container.querySelector(EXISTING_FORM_OVERLAY_SELECTOR)
}

const getTrapRoot = container => getExistingFormOverlay(container) || container

/**
 * Custom hook for implementing focus trap in modals.
 * Stores document.activeElement when activated and restores it on deactivate.
 *
 * @param {boolean} isActive - Whether the focus trap should be active.
 * @param {React.RefObject<HTMLElement>} containerRef - Reference to the modal container.
 * @returns {void}
 */
const useFocusTrap = (isActive, containerRef) => {
  const previousActiveElement = useRef(null)
  const addedTabindexRef = useRef(false)

  useEffect(() => {
    installInteractionListenerOnce()
    const container = containerRef.current

    if (!isActive || !container) {
      return undefined
    }

    const activeAtOpen = document.activeElement
    const isUselessActive =
      activeAtOpen === document.body ||
      activeAtOpen === document.documentElement
    previousActiveElement.current =
      isUselessActive && lastInteractionFocusable
        ? lastInteractionFocusable
        : activeAtOpen
    let hasFocusedContent = false

    const getFocusableElements = () => {
      const root = getTrapRoot(container)
      return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(el =>
        isFocusableVisible(el)
      )
    }

    const getContentFocusableElements = () => {
      const overlay = getExistingFormOverlay(container)
      if (overlay) {
        return Array.from(
          overlay.querySelectorAll(FOCUSABLE_SELECTOR)
        ).filter(el => isFocusableVisible(el))
      }
      return Array.from(container.querySelectorAll('[data-modal-content]'))
        .flatMap(content =>
          Array.from(content.querySelectorAll(FOCUSABLE_SELECTOR))
        )
        .filter(el => isFocusableVisible(el))
    }

    const getInitialFocusElement = focusableElements => {
      const autofocusElement = focusableElements.find(element =>
        element.hasAttribute('autofocus')
      )
      if (autofocusElement) return autofocusElement

      const contentFocusableElements = getContentFocusableElements()
      if (contentFocusableElements.length > 0) {
        hasFocusedContent = true
        return contentFocusableElements[0]
      }

      return (
        focusableElements.find(element =>
          element.hasAttribute('data-fallback-focus')
        ) || focusableElements[0]
      )
    }

    const clearFallbackTabindex = () => {
      if (addedTabindexRef.current) {
        container.removeAttribute('tabindex')
        addedTabindexRef.current = false
      }
    }

    const focusInitial = () => {
      const focusableElements = getFocusableElements()
      const overlay = getExistingFormOverlay(container)

      if (focusableElements.length > 0) {
        clearFallbackTabindex()
        const active = document.activeElement
        const activeEl = active instanceof HTMLElement ? active : null
        const focusTrappedOutsideOverlay =
          overlay &&
          activeEl &&
          container.contains(activeEl) &&
          !overlay.contains(activeEl)

        if (
          focusTrappedOutsideOverlay ||
          !container.contains(active) ||
          active === container
        ) {
          getInitialFocusElement(focusableElements).focus()
        }
        return
      }

      if (!container.hasAttribute('tabindex')) {
        container.setAttribute('tabindex', '-1')
        addedTabindexRef.current = true
      }
      container.focus()
    }

    let rafInner
    const rafOuter = requestAnimationFrame(() => {
      rafInner = requestAnimationFrame(focusInitial)
    })

    const observer = new MutationObserver(() => {
      requestAnimationFrame(() => {
        const active = document.activeElement
        const overlay = getExistingFormOverlay(container)
        if (overlay) {
          const overlayFocusables = Array.from(
            overlay.querySelectorAll(FOCUSABLE_SELECTOR)
          ).filter(el => isFocusableVisible(el))
          if (
            overlayFocusables.length > 0 &&
            !overlayFocusables.includes(active)
          ) {
            overlayFocusables[0].focus()
            return
          }
        }
        const contentFocusableElements = getContentFocusableElements()
        if (
          !hasFocusedContent &&
          contentFocusableElements.length > 0 &&
          !contentFocusableElements.includes(active)
        ) {
          hasFocusedContent = true
          contentFocusableElements[0].focus()
          return
        }

        if (!container.contains(active) || active === container) {
          focusInitial()
        }
      })
    })
    observer.observe(container, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true,
    })

    const handleKeyDown = event => {
      if (event.key !== 'Tab') {
        return
      }

      let currentFocusableElements = getFocusableElements()
      const trapRoot = getTrapRoot(container)
      const activeEl = document.activeElement

      if (
        currentFocusableElements.length > 0 &&
        !(activeEl instanceof HTMLElement && trapRoot.contains(activeEl))
      ) {
        event.preventDefault()
        const target = event.shiftKey
          ? currentFocusableElements[currentFocusableElements.length - 1]
          : currentFocusableElements[0]
        target.focus()
        return
      }

      if (currentFocusableElements.length === 0) {
        event.preventDefault()
        container.focus()
        return
      }

      const firstFocusableElement = currentFocusableElements[0]
      const lastFocusableElement =
        currentFocusableElements[currentFocusableElements.length - 1]
      const activeIsTrapContainer =
        document.activeElement === container ||
        document.activeElement === trapRoot

      if (activeIsTrapContainer) {
        event.preventDefault()
        const target = event.shiftKey
          ? lastFocusableElement
          : firstFocusableElement
        target.focus()
        return
      }

      if (event.shiftKey && document.activeElement === firstFocusableElement) {
        event.preventDefault()
        lastFocusableElement.focus()
        return
      }

      if (!event.shiftKey && document.activeElement === lastFocusableElement) {
        event.preventDefault()
        firstFocusableElement.focus()
      }
    }

    const handleFocusIn = event => {
      const target = event.target
      if (!(target instanceof Node)) return

      const trapRoot = getTrapRoot(container)
      if (container.contains(target) && !trapRoot.contains(target)) {
        requestAnimationFrame(focusInitial)
        return
      }

      if (trapRoot.contains(target)) {
        return
      }

      requestAnimationFrame(focusInitial)
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('focusin', handleFocusIn)

    return () => {
      cancelAnimationFrame(rafOuter)
      if (rafInner != null) {
        cancelAnimationFrame(rafInner)
      }
      observer.disconnect()
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('focusin', handleFocusIn)
      clearFallbackTabindex()

      const prev = previousActiveElement.current
      if (!prev || typeof prev.focus !== 'function') return
      if (prev === document.body || prev === document.documentElement) return
      if (prev.isConnected === false) return
      prev.focus()
    }
  }, [isActive, containerRef])
}

export default useFocusTrap
